// ORDER = the physical walk-around a host actually does, to minimise friction:
// exterior front → open the hood ONCE and take all four engine-bay shots
// (área de motor, número de chasis, larguero izq/der) back-to-back → close it →
// exterior rear → trunk → interior → optional accessories. Before, the two
// "larguero y chasis (desde el área del motor)" shots sat at #10/#11, forcing the
// host to reopen the hood after having closed it at #4 — the friction a real host
// flagged. The `id`s are stable (legacy names); only the sequence changed.
export const LISTING_PHOTO_REQUIREMENTS = [
  {
    id: 'front_right',
    labelKey: 'listing.photo_requirements.front_right',
    helperKey: 'listing.photo_requirement_hints.front_right',
  },
  {
    id: 'front_left',
    labelKey: 'listing.photo_requirements.front_left',
    helperKey: 'listing.photo_requirement_hints.front_left',
  },
  {
    id: 'hood_open',
    labelKey: 'listing.photo_requirements.hood_open',
    helperKey: 'listing.photo_requirement_hints.hood_open',
  },
  {
    id: 'engine_closeup',
    labelKey: 'listing.photo_requirements.engine_closeup',
    helperKey: 'listing.photo_requirement_hints.engine_closeup',
  },
  {
    // "Larguero y chasis izquierdo" — engine bay, hood must be open. Kept next to
    // the other hood-open shots so the host never reopens the hood.
    id: 'rim_complete',
    labelKey: 'listing.photo_requirements.rim_complete',
    helperKey: 'listing.photo_requirement_hints.rim_complete',
  },
  {
    // "Larguero y chasis derecho" — engine bay, hood open.
    id: 'rim_serial',
    labelKey: 'listing.photo_requirements.rim_serial',
    helperKey: 'listing.photo_requirement_hints.rim_serial',
  },
  {
    id: 'rear_right',
    labelKey: 'listing.photo_requirements.rear_right',
    helperKey: 'listing.photo_requirement_hints.rear_right',
  },
  {
    id: 'rear_left',
    labelKey: 'listing.photo_requirements.rear_left',
    helperKey: 'listing.photo_requirement_hints.rear_left',
  },
  {
    id: 'trunk_panorama',
    labelKey: 'listing.photo_requirements.trunk_panorama',
    helperKey: 'listing.photo_requirement_hints.trunk_panorama',
  },
  {
    id: 'spare_tire',
    labelKey: 'listing.photo_requirements.spare_tire',
    helperKey: 'listing.photo_requirement_hints.spare_tire',
  },
  {
    id: 'tools',
    labelKey: 'listing.photo_requirements.tools',
    helperKey: 'listing.photo_requirement_hints.tools',
  },
  {
    id: 'dashboard_panorama',
    labelKey: 'listing.photo_requirements.dashboard_panorama',
    helperKey: 'listing.photo_requirement_hints.dashboard_panorama',
  },
  {
    id: 'dashboard_on',
    labelKey: 'listing.photo_requirements.dashboard_on',
    helperKey: 'listing.photo_requirement_hints.dashboard_on',
  },
  {
    id: 'odometer',
    labelKey: 'listing.photo_requirements.odometer',
    helperKey: 'listing.photo_requirement_hints.odometer',
  },
  {
    id: 'front_seats',
    labelKey: 'listing.photo_requirements.front_seats',
    helperKey: 'listing.photo_requirement_hints.front_seats',
  },
  {
    id: 'exterior_accessories',
    labelKey: 'listing.photo_requirements.exterior_accessories',
    helperKey: 'listing.photo_requirement_hints.exterior_accessories',
  },
  {
    id: 'interior_accessories',
    labelKey: 'listing.photo_requirements.interior_accessories',
    helperKey: 'listing.photo_requirement_hints.interior_accessories',
  },
] as const

export type ListingPhotoRequirementId = (typeof LISTING_PHOTO_REQUIREMENTS)[number]['id']

export const LISTING_PHOTO_REQUIREMENT_IDS = LISTING_PHOTO_REQUIREMENTS.map(
  (requirement) => requirement.id,
) as [ListingPhotoRequirementId, ...ListingPhotoRequirementId[]]

export const MIN_LISTING_PHOTOS = LISTING_PHOTO_REQUIREMENTS.length

/**
 * Slots a host may fill MORE THAN ONCE. One accessory photo cannot show a 360°
 * camera, the 4x4 selector and a subwoofer at the same time — a real host asked
 * for this. Every one of these beyond the first is OPTIONAL: the required count
 * is still MIN_LISTING_PHOTOS (one per slot), extras only add to the listing.
 * Every other slot stays exactly-once so the checklist keeps its meaning.
 */
export const REPEATABLE_PHOTO_REQUIREMENT_IDS: readonly ListingPhotoRequirementId[] = [
  'exterior_accessories',
  'interior_accessories',
]

export function isRepeatablePhotoRequirement(id: ListingPhotoRequirementId): boolean {
  return REPEATABLE_PHOTO_REQUIREMENT_IDS.includes(id)
}

/** Cap per repeatable slot — generous for real extras, bounded for upload time. */
export const MAX_PHOTOS_PER_REPEATABLE_SLOT = 5

/**
 * The storage key for one photo — derived from its IDENTITY, never random and never positional.
 *
 * Every upload used to mint `crypto.randomUUID()` with `upsert: false`, so a retry after a
 * timeout wrote a SECOND object and a SECOND `vehicle_images` row for the same slot. Two rows
 * in a non-repeatable slot make the car impossible to edit — the client schema demands exactly
 * one per slot — and there is no control in the app that can delete the extra.
 *
 * A photo's identity has two shapes, matching `vehicle_images.slot_index`:
 *   * exactly-once slots: the SLOT is the identity. A retake replaces — same key, new bytes.
 *   * repeatable accessory slots: the CONTENT is the identity, so `contentSha` joins the key.
 *     The first design numbered extras by their position in the upload batch, and a retry
 *     uploads a FILTERED batch — the survivors renumbered and an extra could land on its
 *     sibling's key. Content cannot renumber: the same photo is always itself, a different
 *     photo is always different, whatever the batch looks like.
 *
 * The first path segment must stay the vehicle id: `vehicles_host_insert` on the storage bucket
 * keys ownership on `storage.foldername(name))[1]`, and anything else is refused outright.
 */
export function listingPhotoSlotKey(
  requirementId: ListingPhotoRequirementId,
  contentSha?: string | null,
): string {
  return contentSha ? `${requirementId}-${contentSha.slice(0, 16)}` : requirementId
}

export type ListingPhotoCaptureSource = 'tago_live_camera'

/**
 * A capture whose bytes ALREADY sit in the per-user staging area of the vehicles bucket,
 * uploaded in the background while the host was still walking around the car.
 *
 * At publish, a photo with one of these is COPIED server-side to its final
 * `{vehicleId}/{slot}.jpg` key instead of being uploaded again — copies move no bytes over the
 * phone's connection, which is what removes the photos-phase timeout at the root. Everything
 * the DB row needs (hash, blurhash, dimensions) was computed once at staging time and rides
 * along here, so the copy path never has to re-read the image.
 *
 * `sourceKey` is the contract: a staged record is only usable for the photo whose LOCAL uri it
 * was made from. A retake produces a new uri, so a stale staged record simply stops matching
 * and that photo falls back to a direct upload — wrong bytes can never win over fresh ones.
 *
 * A DIGEST of the uri, never the uri itself. On web a capture's uri IS the JPEG — a `data:`
 * string of megabytes — and this record persists in the draft's LIGHT localStorage key, the one
 * the split-storage work exists to keep small. Keying the map by raw uri would have carried the
 * photo bytes right back into the partition they were evicted from.
 */
export interface StagedListingPhoto {
  /** SHA-256 hex of the local uri STRING these bytes came from. The matching key. */
  sourceKey: string
  /** Object key inside the vehicles bucket: `staging/{uid}/{sha256}.jpg`. */
  stagedPath: string
  photoSha256: string
  blurhash: string | null
  width: number
  height: number
  sizeBytes: number
}

export interface ListingPhoto {
  uri: string
  width?: number
  height?: number
  capturedAt: string
  source: ListingPhotoCaptureSource
  requirementId: ListingPhotoRequirementId
}

/**
 * Canonical presentation order for vehicle photos in public marketing surfaces
 * (vehicle detail hero, carousels, cards).
 *
 * Designed to show the most visually appealing exterior angles first, followed
 * by the cockpit, interior comfort and trunk space, while placing technical
 * mechanical and chassis inspection photos at the end (for dedicated verification
 * sections).
 */
export const SHOWCASE_PHOTO_ORDER: readonly ListingPhotoRequirementId[] = [
  'front_right', // 0: Hero 3/4 exterior front
  'front_left', // 1: Opposite 3/4 exterior front
  'rear_right', // 2: 3/4 exterior rear
  'rear_left', // 3: Opposite 3/4 exterior rear
  'dashboard_panorama', // 4: Cabin cockpit / dashboard
  'front_seats', // 5: Seats / interior upholstery
  'dashboard_on', // 6: Digital cluster / infotainment
  'trunk_panorama', // 7: Trunk / cargo capacity
  'exterior_accessories', // 8: Rims, sunroof, exterior extras
  'interior_accessories', // 9: Audio system, interior extras
  // Mechanical inspection & compliance (ASR / Qualitas):
  'hood_open', // 10: Engine bay / open hood
  'engine_closeup', // 11: Engine close-up
  'rim_complete', // 12: Chassis rail left
  'rim_serial', // 13: Chassis rail right
  'spare_tire', // 14: Spare tire
  'tools', // 15: Emergency tools / jack
  'odometer', // 16: Odometer reading
] as const

/**
 * Requirement IDs specifically used for mechanical and compliance verification (ASR / Qualitas).
 * These are separated into the dedicated "Inspección mecánica y seguridad ASR" section on vehicle detail.
 */
export const INSPECTION_PHOTO_REQUIREMENT_IDS: readonly ListingPhotoRequirementId[] = [
  'hood_open',
  'engine_closeup',
  'rim_complete',
  'rim_serial',
  'spare_tire',
  'tools',
  'odometer',
] as const

export function isInspectionPhotoRequirement(
  id?: ListingPhotoRequirementId | string | null,
): boolean {
  if (!id) return false
  return (INSPECTION_PHOTO_REQUIREMENT_IDS as readonly string[]).includes(id)
}

/**
 * Returns the canonical showcase position (0..16) for a given requirement ID.
 * Falls back cleanly for repeatable extras or legacy photos.
 */
export function getShowcasePosition(
  requirementId?: ListingPhotoRequirementId | string | null,
  fallbackIndex = 0,
): number {
  if (requirementId) {
    const idx = (SHOWCASE_PHOTO_ORDER as readonly string[]).indexOf(
      requirementId as ListingPhotoRequirementId,
    )
    if (idx !== -1) return idx
  }
  return fallbackIndex
}

/**
 * Returns a numerical sort priority for any photo.
 * Showcase photos get 0..9, inspection photos get 10..16, and unclassified photos follow their existing position.
 */
export function getPhotoDisplayPriority(
  requirementId?: string | null,
  position?: number | null,
): number {
  if (requirementId) {
    const idx = (SHOWCASE_PHOTO_ORDER as readonly string[]).indexOf(
      requirementId as ListingPhotoRequirementId,
    )
    if (idx !== -1) return idx
  }
  return typeof position === 'number' ? position + 100 : 999
}

/**
 * Sorts any array of vehicle images into canonical showcase order.
 * Ensures the most attractive photos (front, rear, cabin) appear first in galleries and carousels,
 * while technical inspection photos are cleanly ordered at the end.
 */
export function sortVehicleImagesForShowcase<
  T extends { requirementId?: string | null; position?: number | null; isPrimary?: boolean },
>(images: readonly T[] | null | undefined): T[] {
  if (!images || images.length === 0) return []
  return images.slice().sort((a, b) => {
    const pA = getPhotoDisplayPriority(a.requirementId, a.position)
    const pB = getPhotoDisplayPriority(b.requirementId, b.position)
    if (pA !== pB) return pA - pB
    if (a.isPrimary && !b.isPrimary) return -1
    if (!a.isPrimary && b.isPrimary) return 1
    return (a.position ?? 0) - (b.position ?? 0)
  })
}
